import { createResource } from "~/lib/resourceFactory";
import { getBalanceSheet } from "./balance-sheet.api";

export type BalanceSheetParams = {
  businessId: string;
  asOf?: string;
};

export type BalanceSheetData = Awaited<ReturnType<typeof getBalanceSheet>>;

function toIsoDate(d: Date): string {
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
}

/** Today's date as an ISO string. */
export function todayIso(): string {
  return toIsoDate(new Date());
}

/**
 * The default params the balance sheet report renders with: as of today. The
 * route loader seeds the query cache with these exact params so the component's
 * createQuery hook finds data on the first paint (no "pop in").
 */
export function defaultBalanceSheetParams(
  businessId: string,
): BalanceSheetParams {
  return { businessId, asOf: todayIso() };
}

export const BalanceSheetResource = createResource<
  BalanceSheetData,
  BalanceSheetParams
>({
  key: (p) =>
    ["reports", "balance-sheet", p.businessId, p.asOf ?? "*"] as const,
  fetcher: (p) =>
    getBalanceSheet({
      data: {
        businessId: p.businessId,
        asOf: p.asOf,
      },
    }),
  // Avoid suspending SSR when the query is disabled (no active business).
  placeholderData: {
    asOf: "",
    assets: [],
    liabilities: [],
    equity: [],
    totalAssets: 0,
    totalLiabilities: 0,
    totalEquity: 0,
  },
});
